import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Mail, Clock, FolderKanban, RefreshCw, Search, ListChecks, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import CalendarFilter from '../common/CalendarFilter';
import { teamService } from '../../services/teamService';
import { exportMemberTimesheet } from '../../utils/memberTimesheetExcel';

const getDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getThisMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: getDateStr(start), endDate: getDateStr(end) };
};

export default function TeamMemberDetail({ member, onBack }) {
  const initialRange = useMemo(getThisMonthRange, []);
  const [dateRange, setDateRange] = useState(initialRange);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [groupBy, setGroupBy] = useState('none'); // none | issue_status | issue_priority

  const fetchWorklogs = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const result = await teamService.getMemberWorklogs(member.id, {
        startDate: appliedRange.startDate,
        endDate: appliedRange.endDate,
        refresh,
      });
      setData(result);
      const projects = result?.projects ?? [];
      setActiveProject(projects.length ? projects[0].project_key : null);
      if (refresh) toast.success('Worklogs refreshed');
    } catch (err) {
      toast.error(err.message || 'Failed to load worklogs');
      setData(null);
      setActiveProject(null);
    } finally {
      setLoading(false);
    }
  }, [member.id, appliedRange.startDate, appliedRange.endDate]);

  const [hasApplied, setHasApplied] = useState(false);

  // Don't auto-load on entry  only fetch after the user applies a range.
  useEffect(() => {
    if (!hasApplied) return;
    fetchWorklogs();
  }, [fetchWorklogs, hasApplied]);

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const rangeChanged =
    dateRange.startDate !== appliedRange.startDate || dateRange.endDate !== appliedRange.endDate;

  const applyRange = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error('Please select both a start and end date');
      return;
    }
    setHasApplied(true);
    setAppliedRange({ ...dateRange });
  };

  const projects = data?.projects ?? [];
  const totals = data?.totals ?? { total_hours: 0, total_entries: 0, project_count: 0 };
  const current = useMemo(
    () => projects.find((p) => p.project_key === activeProject) ?? null,
    [projects, activeProject]
  );
  const groupedIssues = useMemo(() => {
    const issues = current?.issues ?? [];
    if (groupBy === 'none') return null; // flat table

    const groups = issues.reduce((acc, issue) => {
      const key =
        groupBy === 'issue_status'
          ? (issue.issue_status || 'No Status')
          : (issue.issue_priority || 'No Priority');
      (acc[key] ||= { key, issues: [], hours: 0, entries: 0 });
      acc[key].issues.push(issue);
      acc[key].hours += Number(issue.logged_hours) || 0;
      acc[key].entries += Number(issue.entries) || 0;
      return acc;
    }, {});

    return Object.values(groups).sort((a, b) => b.hours - a.hours);
  }, [current, groupBy]);

  const handleExport = () => {
    setExporting(true);
    try {
      exportMemberTimesheet({
        memberName: member.name,
        projects,
        startDate: appliedRange.startDate,
        endDate: appliedRange.endDate,
      });
    } catch (err) {
      toast.error('Failed to export worklogs');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Member header */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-5">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to team
        </button>

        <div className="flex items-center gap-4">
          <img
            src={member.avatar_url}
            alt=""
            className="size-14 rounded-full shrink-0 bg-slate-200 dark:bg-slate-700"
          />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
              {member.name}
            </h2>
            {member.email && (
              <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 truncate">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                {member.email}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Date range filter */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-5">
        <CalendarFilter filters={dateRange} onFilterChange={handleDateChange} />
        <div className="flex justify-end mt-4">
          <button
            onClick={applyRange}
            disabled={loading || (hasApplied && !rangeChanged)}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Apply
              </>
            )}
          </button>
        </div>
      </div>

      {/* Prompt state — nothing loads until the user applies */}
      {!hasApplied && !loading && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-10 text-center">
          <Search className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" />
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Set your date range and click Apply to load this member's worklogs.
          </p>
        </div>
      )}

      {/* Totals */}
      {!loading && data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryStat label="Total hours" value={`${totals.total_hours}h`} icon={Clock} />
          <SummaryStat label="Projects" value={totals.project_count} icon={FolderKanban} />
          <SummaryStat label="Worklog entries" value={totals.total_entries} icon={ListChecks} />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-10 text-center text-sm text-gray-500 dark:text-slate-400">
          <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin" />
          Fetching worklogs from Jira...
        </div>
      )}

      {/* Empty */}
      {!loading && data && projects.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-10 text-center">
          <FolderKanban className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" />
          <p className="text-sm text-gray-600 dark:text-slate-400">
            No worklogs found for this member in the selected range.
          </p>
        </div>
      )}

      {/* Project tabs + table */}
      {!loading && projects.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 px-2">
            <div className="flex flex-wrap items-center gap-1 gap-y-1.5">
              {projects.map((p) => (
                <button
                  key={p.project_key}
                  onClick={() => setActiveProject(p.project_key)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeProject === p.project_key
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100'
                  }`}
                >
                  {p.project_name || p.project_key}
                  <span className="ml-2 text-xs text-gray-400 dark:text-slate-500">
                    {p.total_hours}h
                  </span>
                </button>
              ))}
            </div>

            <div className="shrink-0 ml-2 flex items-center gap-2">
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                title="Group issues by"
                className="px-3 py-1.5 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer"
              >
                <option value="none">Group by: None</option>
                <option value="issue_status">Group by: Status</option>
                <option value="issue_priority">Group by: Priority</option>
              </select>
              <button
                onClick={() => fetchWorklogs({ refresh: true })}
                disabled={loading}
                title="Fetch the latest hours from Jira (bypass cache)"
                className="inline-flex items-center px-3 py-1.5 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-white/10 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex items-center px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1.5" />
                    Export
                  </>
                )}
              </button>
            </div>
          </div>

          {current && (
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                  {current.project_name || current.project_key}
                </h3>
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  {current.total_hours}h across {current.issues.length} issue
                  {current.issues.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 border-b border-gray-200 dark:border-white/10">
                      <th className="py-2 pr-4 font-medium">Issue</th>
                      <th className="py-2 pr-4 font-medium">Summary</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Priority</th>
                      <th className="py-2 pr-4 font-medium text-right">Hours</th>
                      <th className="py-2 font-medium text-right">Entries</th>
                    </tr>
                  </thead>

                  {groupBy === 'none' ? (
                    <tbody>
                      {current.issues.map((issue) => (
                        <tr
                          key={issue.issue_key}
                          className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5"
                        >
                          <td className="py-2.5 pr-4 font-mono text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                            {issue.issue_key}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-900 dark:text-slate-100 max-w-xs truncate">
                            {issue.issue_summary}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-600 dark:text-slate-300 whitespace-nowrap">
                            {issue.issue_status}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-600 dark:text-slate-300 whitespace-nowrap">
                            {issue.issue_priority || '—'}
                          </td>
                          <td className="py-2.5 pr-4 text-right font-medium text-gray-900 dark:text-slate-100 whitespace-nowrap">
                            {issue.logged_hours}h
                          </td>
                          <td className="py-2.5 text-right text-gray-600 dark:text-slate-300">
                            {issue.entries}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    groupedIssues.map((group) => (
                      <tbody key={group.key} className="border-b border-gray-200 dark:border-white/10">
                        <tr className="bg-gray-50 dark:bg-white/5">
                          <td colSpan={4} className="py-2 pr-4 font-semibold text-gray-900 dark:text-slate-100">
                            {group.key}
                            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400">
                              ({group.issues.length} issue{group.issues.length === 1 ? '' : 's'})
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-right font-semibold text-gray-900 dark:text-slate-100 whitespace-nowrap">
                            {group.hours}h
                          </td>
                          <td className="py-2 text-right font-semibold text-gray-600 dark:text-slate-300">
                            {group.entries}
                          </td>
                        </tr>
                        {group.issues.map((issue) => (
                          <tr
                            key={issue.issue_key}
                            className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5"
                          >
                            <td className="py-2.5 pr-4 font-mono text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">
                              {issue.issue_key}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-900 dark:text-slate-100 max-w-xs truncate">
                              {issue.issue_summary}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-600 dark:text-slate-300 whitespace-nowrap">
                              {issue.issue_status}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-600 dark:text-slate-300 whitespace-nowrap">
                              {issue.issue_priority || '—'}
                            </td>
                            <td className="py-2.5 pr-4 text-right font-medium text-gray-900 dark:text-slate-100 whitespace-nowrap">
                              {issue.logged_hours}h
                            </td>
                            <td className="py-2.5 text-right text-gray-600 dark:text-slate-300">
                              {issue.entries}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ))
                  )}

                  <tfoot>
                    <tr className="font-semibold text-gray-900 dark:text-white">
                      <td className="py-2.5 pr-4" colSpan={4}>
                        Total
                      </td>
                      <td className="py-2.5 pr-4 text-right whitespace-nowrap">
                        {current.total_hours}h
                      </td>
                      <td className="py-2.5 text-right">{current.total_entries}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4 flex items-center gap-3">
      <div className="p-2 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-slate-400">{label}</p>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}